import React, { useCallback, useEffect, useMemo } from 'react';
import config from 'config';

import { useTranslation } from '@lyearn/i18n';
import { CloseOutline, FileError, FilePdf, FilePpt, PlusOutline } from '@lyearn/icons';
import { UploadedFile, useUppy } from '@lyearn/shared/hooks/useUppy';
import { cn, css } from '@lyearn/style';
import { Box, Button, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { useUppyLazyGetInstance } from '@/components/UppyProvider';
import UploadBox from '@/features/media/components/UploadBox';
import currentUser from '@/helper/currentUser';
import { request } from '@/helper/request';
import { useAppConfig } from '@/hooks/useAppConfig';
import { ResourceResponseTypeEnum } from '@/types/schema';
import fileSize from '@/utils/fileSize';

const MAX_SIZE_VIDEO = 1073741824; // 1gb
const MAX_SIZE_AUDIO = 104857600; // 100mb
const MAX_SIZE_DEFAULT = 268435456; // 256mb

const getMaxFileSizeForType = (mimeType?: string, fileName?: string): number => {
  if (!mimeType && !fileName) return MAX_SIZE_DEFAULT;

  // Check by mime type
  if (mimeType?.startsWith('video/')) return MAX_SIZE_VIDEO;
  if (mimeType?.startsWith('audio/')) return MAX_SIZE_AUDIO;

  // Check by file extension if mime type is not available
  const lowerFileName = fileName?.toLowerCase() || '';
  if (lowerFileName.endsWith('.mp4') || lowerFileName.endsWith('.mov')) {
    return MAX_SIZE_VIDEO;
  }
  if (lowerFileName.endsWith('.mp3') || lowerFileName.endsWith('.wav')) {
    return MAX_SIZE_AUDIO;
  }

  return MAX_SIZE_DEFAULT;
};

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(0)}GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)}MB`;
  return `${(bytes / 1024).toFixed(0)}KB`;
};

const SLIDE_DECK_ACCEPT = [
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
].join(',');

const styleAsIconButton = css`
  min-width: 0 !important;
  padding-inline: 0.6rem !important;
  & .MuiButton-startIcon {
    margin: 0;
  }
`;

interface SlideDeckUploadProps {
  blockId?: string;
  slideDeckUrl?: string | null;
  slideDeckFileName?: string | null;
  onUpload: (input: { url: string; fileName: string | null }) => void;
  onRemove?: () => void;
}

const SlideDeckUpload: React.FC<SlideDeckUploadProps> = ({
  blockId,
  slideDeckUrl,
  slideDeckFileName,
  onUpload,
  onRemove,
}) => {
  const { t } = useTranslation('Course');
  const { site: siteConfig, restrictedFileExtensions } = useAppConfig();
  const { getUppyInstance, isReady } = useUppyLazyGetInstance();
  const { showErrorSnackbar } = useSnackbar();

  // create a new uppy instance for each slide deck uploader
  const uppyInstance = useMemo(() => {
    if (!isReady || !getUppyInstance) return null;

    const instance = getUppyInstance(
      config.uppy_companion_url,
      {
        getAccessToken: currentUser._aT,
        disabledMultipartUpload: siteConfig.disabledMultipartUpload,
        restrictedFileExtensions,
        restrictions: { maxFileSize: MAX_SIZE_VIDEO },
      },
      request,
    );

    // Add custom file size validation based on file type
    instance.on('file-added', (file) => {
      const maxSize = getMaxFileSizeForType(file.type, file.name);
      if (file.size && file.size > maxSize) {
        const fileType = file.type?.startsWith('video/')
          ? 'Video'
          : file.type?.startsWith('audio/')
          ? 'Audio'
          : 'File';
        instance.info(
          `${fileType} files must be less than ${formatFileSize(
            maxSize,
          )}. Your file is ${formatFileSize(file.size)}.`,
          'error',
          5000,
        );
        instance.removeFile(file.id);
      }
    });

    return instance;
  }, [getUppyInstance, isReady, siteConfig.disabledMultipartUpload, restrictedFileExtensions]);

  const slideDeckUploadData = useUppy(
    {
      referenceId: blockId ? `ai-video-slide-deck-${blockId}` : 'ai-video-slide-deck',
      onUploadSuccess: async (file: UploadedFile) => {
        if (file.url) {
          onUpload({
            url: file.url,
            fileName: file.name ?? null,
          });
        }
        return file;
      },
      onRestrictedFile: (file, error) => {
        showErrorSnackbar({ message: error.message });
      },
    },
    uppyInstance,
  );

  // clean up uppy instance when component unmounts
  useEffect(() => {
    return () => {
      uppyInstance?.close();
    };
  }, [uppyInstance]);

  const slideDeckFiles = useMemo(
    () => Object.values(slideDeckUploadData.files ?? {}),
    [slideDeckUploadData.files],
  );
  const latestSlideDeckFile = slideDeckFiles[slideDeckFiles.length - 1] as any;
  const hasSlideDeckFiles = slideDeckFiles.length > 0;
  const isSlideDeckUploading = slideDeckUploadData.isUploading;
  const slideDeckUploadProgress = Math.max(
    0,
    Math.min(100, Math.round(slideDeckUploadData.uppy?.getState()?.totalProgress ?? 0)),
  );
  const slideDeckHasError = Boolean(latestSlideDeckFile?.error);
  const showSlideDeckStatus = Boolean(
    slideDeckUrl || hasSlideDeckFiles || isSlideDeckUploading || slideDeckHasError,
  );

  const handleSlideDeckRemove = useCallback(() => {
    slideDeckUploadData.clearAll();
    onRemove?.();
  }, [onRemove, slideDeckUploadData]);

  const uploadedSlideDeckFileName = slideDeckUrl
    ? slideDeckFileName ||
      decodeURIComponent(slideDeckUrl.split('/').pop()?.split('?')[0] || 'Slide Deck')
    : latestSlideDeckFile?.name || 'Slide Deck';
  const slideDeckMimeType = latestSlideDeckFile?.type || '';
  const slideDeckSourceName = `${latestSlideDeckFile?.name || ''} ${slideDeckFileName || ''} ${
    slideDeckUrl || ''
  }`;
  const isPptUpload =
    /presentationml|ms-powerpoint/i.test(slideDeckMimeType) ||
    /\.(ppt|pptx)(\?|$)/i.test(slideDeckSourceName);

  let uploadedSlideDeckDescription = '';
  if (isSlideDeckUploading) {
    uploadedSlideDeckDescription = t('Course:AiVideoGeneration.Uploading');
  } else if (slideDeckHasError) {
    uploadedSlideDeckDescription = t('Common:SomethingWentWrong');
  } else if ((latestSlideDeckFile?.size ?? 0) > 0) {
    uploadedSlideDeckDescription = fileSize(latestSlideDeckFile?.size ?? 0) as string;
  }

  return (
    <UploadBox
      accept={SLIDE_DECK_ACCEPT}
      files={slideDeckUploadData.files}
      multiple={false}
      renderer={(showFileSelector) =>
        showSlideDeckStatus ? (
          <Stack
            alignItems="center"
            className={cn(
              'relative w-full overflow-hidden rounded-8 border border-solid px-24 py-20',
              slideDeckHasError ? 'border-border-error-dark' : 'border-interactive-secondary',
              'shadow-01',
            )}
            direction="row"
            justifyContent="between">
            <Box className="me-24">
              {slideDeckHasError ? <FileError /> : isPptUpload ? <FilePpt /> : <FilePdf />}
            </Box>
            <Stack className="min-w-0 grow" direction="col">
              <Typography className="truncate" title={uploadedSlideDeckFileName} variant="label-01">
                {uploadedSlideDeckFileName}
              </Typography>
              <Typography color="text-text-secondary" variant="body-short-02">
                {uploadedSlideDeckDescription}
              </Typography>
            </Stack>
            {onRemove && !isSlideDeckUploading ? (
              <Button
                StartIcon={CloseOutline}
                className={styleAsIconButton}
                size="x-small"
                variant="ghost"
                onClick={handleSlideDeckRemove}
              />
            ) : null}
            {isSlideDeckUploading ? (
              <Box
                className="absolute bottom-0 start-0 h-4 w-full rounded-full bg-icon-success"
                style={{ width: `${slideDeckUploadProgress}%` }}
              />
            ) : null}
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            className="w-full cursor-pointer rounded-8 border border-dashed border-border-dark bg-white px-24 py-20"
            direction="row"
            justifyContent="between"
            role="button"
            tabIndex={0}
            onClick={showFileSelector}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && showFileSelector()}>
            <Stack alignItems="center" direction="row" gap={16}>
              <Box className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-[4px] bg-bg-tertiary">
                <PlusOutline className="text-icon-secondary" height={20} width={20} />
              </Box>
              <Stack direction="col">
                <Typography color="text-text-paragraph" variant="body-short-01">
                  {t('Course:AiVideoGeneration.UploadSlideDeck')}
                </Typography>
                <Typography color="text-text-placeholder" variant="body-short-02">
                  (.pptx, .pdf, max 256 MB)
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )
      }
      type={ResourceResponseTypeEnum.File}
      uppy={slideDeckUploadData.uppy}
    />
  );
};

export default SlideDeckUpload;
